// Controller for the `teams` page (/teams). ARCHITECTURE §5.2, §6.1 C3.
// State and the one effect move verbatim from Teams.tsx (release/1.0 @
// 21609c5): teams refetch on a sport switch; the search filters client-side.
// Fetch, stale guard, error, retry and first load: usePageResource (#1291).
// useSport() for the VM (a context read).
// #583: with 2+ live divisions the list is `?division=<id>` (one division) or
// `?division=all` (every team, grouped by `division_ids`). Below that, the
// division arg is undefined and the request is exactly today's.

import { useState } from 'react'
import { useLeague } from '@/contexts/LeagueContext'
import { useSport, useSportContext } from '@/contexts/SportContext'
import { api } from '@/services/api'
import { divisionArg, useDivision } from '../../division'
import { usePageResource } from '../usePageResource'
import { filterTeams, groupTeamsByDivision, teamCard } from './derive'
import type { TeamsPageVM } from './types'

export default function useTeamsPage(): TeamsPageVM {
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  const tenantName = league?.name ?? 'League'
  const [searchQuery, setSearchQuery] = useState('')
  const sport = useSport()
  const division = useDivision()
  const divArg = divisionArg(division)

  const res = usePageResource(() => api.getTeams(undefined, { division: divArg }).then((r) => r.teams ?? []), [activeSportId, divArg])
  const teams = res.data ?? []
  const items = filterTeams(teams, searchQuery).map(teamCard)

  return {
    status: res.status,
    error: res.error,
    retry: res.retry,
    refreshing: res.refreshing,
    leagueName: tenantName,
    sport,
    search: { value: searchQuery, debounced: searchQuery, set: setSearchQuery },
    items,
    count: items.length,
    total: teams.length,
    division,
    groups: divArg === 'all' ? groupTeamsByDivision(items, division.live) : null,
  }
}
